/**
 * Conditional-block marker expander — FRONTEND PREVIEW port.
 *
 * The email builder (`parse-email.ts`) wraps any block carrying a
 * `displayCondition` in a balanced HTML-comment marker:
 *
 *   <!--VLT-IF:<base64url-json ConditionGroup>-->…block html…<!--VLT-ENDIF-->
 *
 * At REAL send the consumer (`main-consumer/conditional-marker-expander.ts`)
 * decodes each marker, evaluates it against the recipient, and KEEPS or STRIPS
 * the block — the markers never reach the inbox. This is the byte-for-byte FE
 * twin of that expander, so the send / test-mail PREVIEW shows the recipient
 * exactly what they will receive instead of leaving every conditioned block
 * permanently visible (the markers are invisible HTML comments otherwise).
 *
 * Fail-safe: an undecodable payload or an unbalanced open/close KEEPS the
 * content (never hide a block by accident) and still strips the marker.
 * Nesting is supported via a frame stack (innermost-first).
 *
 * Keep aligned with the consumer expander + `evaluate-condition.ts`.
 */
import { ConditionGroup } from "@/types/EventRule";
import { evaluateConditionGroup } from "./evaluate-condition";

const MARKER = /<!--VLT-IF:([A-Za-z0-9_-]+)-->|<!--VLT-ENDIF-->/g;

type Frame = { keep: boolean; buf: string };

export function expandConditionalMarkers(
  html: string,
  guestData: Record<string, unknown> | null | undefined,
): string {
  if (!html || !html.includes("<!--VLT-IF:")) return html;

  const stack: Frame[] = [{ keep: true, buf: "" }];
  let last = 0;
  let m: RegExpExecArray | null;

  MARKER.lastIndex = 0;
  while ((m = MARKER.exec(html)) !== null) {
    // stack always holds at least the root frame — never popped below 1.
    stack[stack.length - 1]!.buf += html.slice(last, m.index);
    last = MARKER.lastIndex;

    if (m[1] !== undefined) {
      // OPEN — decode + evaluate now; undecodable → keep (fail-safe).
      const group = decodeGroup(m[1]);
      const keep = group === null ? true : evaluateConditionGroup(group, guestData);
      stack.push({ keep, buf: "" });
    } else if (stack.length > 1) {
      // CLOSE — append this frame's buffer to its parent only if kept.
      const frame = stack.pop() as Frame;
      if (frame.keep) stack[stack.length - 1]!.buf += frame.buf;
    }
    // stray ENDIF (stack depth 1): drop the marker, keep surrounding text.
  }

  stack[stack.length - 1]!.buf += html.slice(last);

  // Unbalanced opens left on the stack → fail-safe: keep their content.
  while (stack.length > 1) {
    const frame = stack.pop() as Frame;
    stack[stack.length - 1]!.buf += frame.buf;
  }

  return stack[0]!.buf;
}

function decodeGroup(b64url: string): ConditionGroup | null {
  try {
    const normalised = b64url.replace(/-/g, "+").replace(/_/g, "/");
    const pad = normalised.length % 4;
    const padded = pad === 0 ? normalised : normalised + "=".repeat(4 - pad);
    const bin = atob(padded);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const json = new TextDecoder().decode(bytes);
    const raw = JSON.parse(json) as unknown;
    if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return null;
    return raw as ConditionGroup;
  } catch {
    return null;
  }
}
